// i18n
import i18n from 'i18next';

// types
import { MonthNumber, Quarter } from '../@types';

interface TimeUnits {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
}

type TimeUnit = keyof TimeUnits;

export const toRelativeTime = (d1: Date, lang = i18n.language, d2 = new Date()) => {
	const units: TimeUnits = {
		year: 24 * 60 * 60 * 1000 * 365,
		month: (24 * 60 * 60 * 1000 * 365) / 12,
		day: 24 * 60 * 60 * 1000,
		hour: 60 * 60 * 1000,
		minute: 60 * 1000,
		second: 1000,
	};

	const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });

	const elapsed = d1.getTime() - d2.getTime();

	for (const u in units) {
		const ms = units[u as TimeUnit];
		if (Math.abs(elapsed) > ms || u === 'second') {
			return rtf.format(Math.round(elapsed / ms), u as Intl.RelativeTimeFormatUnit);
		}
	}
};

export interface FormatDateParams {
	date: Date;
	format?: 'YYYY-MM-DD';
	lang?: string;
}

export const formatDate = ({ date, format, lang = i18n.language }: FormatDateParams) => {
	if (!date) {
		return '';
	}

	if (format === 'YYYY-MM-DD') {
		return date.toLocaleDateString('en-CA', {
			timeZone: 'UTC',
		});
	}

	return date.toLocaleDateString(lang, {
		day: 'numeric',
		month: 'short',
		timeZone: 'UTC',
		year: 'numeric',
	});
};

export const toUTC = (date: Date | string) => {
	const d = new Date(date);
	return new Date(
		Date.UTC(
			d.getUTCFullYear(),
			d.getUTCMonth(),
			d.getUTCDate(),
			d.getUTCHours(),
			d.getUTCMinutes(),
			d.getUTCSeconds()
		)
	);
};

export const getDaysInMonth = (month: number) => {
	// Map of months and number of days
	const monthDaysMap = new Map([
		[1, 31], // January
		[2, 28], // February (non-leap year)
		[3, 31], // March
		[4, 30], // April
		[5, 31], // May
		[6, 30], // June
		[7, 31], // July
		[8, 31], // August
		[9, 30], // September
		[10, 31], // October
		[11, 30], // November
		[12, 31], // December
	]);

	return monthDaysMap.get(month) || 31;
};

export interface GetFiscalYearDatesParams {
	fiscalYear: number;
	monthEnd?: MonthNumber;
	monthStart?: MonthNumber;
	quarter?: Quarter;
}

export const getFiscalYearDates = ({
	fiscalYear,
	monthEnd = 5,
	monthStart = 6,
	quarter,
}: GetFiscalYearDatesParams): { dateEnd: Date | null; dateStart: Date | null } => {
	let dateStart: Date | null = null;
	let dateEnd: Date | null = null;

	if (fiscalYear) {
		// fiscal year is July-June by default
		// FY2026 = July 2025 - June 2026
		dateStart = new Date(fiscalYear - 1, monthStart, 1); // monthStart of previous year
		dateEnd = new Date(fiscalYear, monthEnd, getDaysInMonth(monthEnd + 1)); // monthEnd of fiscal year

		if (quarter) {
			// Q1: Jul-Sep, Q2: Oct-Dec, Q3: Jan-Mar, Q4: Apr-Jun (for default July-June FY)
			switch (quarter) {
				case 'Q1':
					return {
						dateEnd: new Date(fiscalYear - 1, monthStart + 2, getDaysInMonth(monthStart + 3)),
						dateStart: new Date(fiscalYear - 1, monthStart, 1),
					};
				case 'Q2':
					return {
						dateEnd: new Date(fiscalYear - 1, monthStart + 5, getDaysInMonth(monthStart + 6)),
						dateStart: new Date(fiscalYear - 1, monthStart + 3, 1),
					};
				case 'Q3':
					return {
						dateEnd: new Date(fiscalYear, monthStart - 4, getDaysInMonth(monthStart - 3)),
						dateStart: new Date(fiscalYear, monthStart - 6, 1),
					};
				case 'Q4':
					return {
						dateEnd: new Date(fiscalYear, monthEnd, getDaysInMonth(monthEnd + 1)),
						dateStart: new Date(fiscalYear, monthEnd - 2, 1),
					};
			}
		}
	}

	return { dateEnd, dateStart };
};

export interface GetDaysBetweenParams {
	from: string | null | undefined;
	to: string | null | undefined;
}

export const getDaysBetween = ({ from, to }: GetDaysBetweenParams): null | number => {
	if (!from || !to) return null;
	const diff = new Date(to).getTime() - new Date(from).getTime();
	return Math.floor(diff / (1000 * 60 * 60 * 24));
};
